import React, { useEffect, useRef } from 'react';

// Recuadro para firmar dibujando con el dedo o el mouse (en vez de adjuntar
// una foto de una firma en papel). onChange recibe un data URL PNG con el
// trazo, o '' cuando el recuadro está vacío/se limpia.
export default function SignaturePad({ onChange, height = 140 }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef(null);
  const hasStrokeRef = useRef(false);

  const fillWhite = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  };

  useEffect(() => { fillWhite(); }, []);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
    const clientX = touch ? touch.clientX : e.clientX;
    const clientY = touch ? touch.clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const start = (e) => {
    if (e.cancelable) e.preventDefault();
    drawingRef.current = true;
    lastPointRef.current = getPos(e);
  };

  const move = (e) => {
    if (!drawingRef.current) return;
    if (e.cancelable) e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e);
    ctx.strokeStyle = '#111827';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPointRef.current = pos;
    hasStrokeRef.current = true;
  };

  // Se llama tanto en touchend como en touchcancel: si el navegador decide a
  // mitad del trazo que el gesto es un scroll (arbitraje táctil) cancela el
  // touch en vez de terminarlo normalmente, y sin esto la firma dibujada se
  // perdía en silencio en dispositivos táctiles reales (no pasaba en mouse).
  const end = (e) => {
    if (!drawingRef.current) return;
    if (e && e.cancelable) e.preventDefault();
    drawingRef.current = false;
    if (hasStrokeRef.current) {
      onChangeRef.current(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    fillWhite();
    hasStrokeRef.current = false;
    onChangeRef.current('');
  };

  // Referencias "siempre actualizadas" para que los listeners nativos (que se
  // registran una sola vez, más abajo) siempre ejecuten la versión más
  // reciente de estas funciones sin necesidad de volver a suscribirse.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const startRef = useRef(start);
  startRef.current = start;
  const moveRef = useRef(move);
  moveRef.current = move;
  const endRef = useRef(end);
  endRef.current = end;

  // Los eventos táctiles sintéticos de React (onTouchStart/onTouchMove) se
  // registran como pasivos en muchos navegadores/WebViews: preventDefault()
  // dentro de ellos no siempre evita que el navegador interprete el trazo
  // como un scroll. Por eso se registran los listeners de touch manualmente,
  // como no pasivos, directamente sobre el canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const onStart = (e) => startRef.current(e);
    const onMove = (e) => moveRef.current(e);
    const onEnd = (e) => endRef.current(e);
    const onCancel = (e) => endRef.current(e);
    canvas.addEventListener('touchstart', onStart, { passive: false });
    canvas.addEventListener('touchmove', onMove, { passive: false });
    canvas.addEventListener('touchend', onEnd, { passive: false });
    canvas.addEventListener('touchcancel', onCancel, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', onStart);
      canvas.removeEventListener('touchmove', onMove);
      canvas.removeEventListener('touchend', onEnd);
      canvas.removeEventListener('touchcancel', onCancel);
    };
  }, []);

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={height}
        className="w-full border rounded-lg bg-white touch-none"
        style={{ height, touchAction: 'none' }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
      />
      <button type="button" onClick={clear} className="text-xs text-blue-600 mt-1">
        Limpiar firma
      </button>
    </div>
  );
}
